import type { AccessTokenResponse, LocationTokenResponse, User } from "~/types/oauth";
import type { CookieRef } from "#app";
import type { ModuleOptions } from "@bitinflow/nuxt-oauth";
import type { NavigationFailure } from "#vue-router";
import type { LocationIdentifier } from "~/types/api";
import { useLocalStorage } from "@vueuse/core";
import { removeOAuthUser, getOAuthUser } from "~/utils/oauthUserStorage";

interface AccessTokenCookie {
  token: string;
  tokenType: string;
  expiresAt: number;
}

interface RefreshTokenCookie {
  token: string;
  tokenType: string;
  expiresAt: number;
}

interface OAuthConfig extends ModuleOptions {
  refreshToken?: {
    maxAge: number;
  };
}

let isSigningOut = false;

let refreshPromise: Promise<boolean> | null = null;
let refreshMutex = false;

export default async function () {
  const { public: config } = useRuntimeConfig();

  const {
    signOut,
    setBearerToken,
    setRefreshToken,
    bearerToken,
    accessToken,
    refreshToken,
    authConfig,
  }: {
    user: CookieRef<User>;
    signOut: () => Promise<void | NavigationFailure | false>;
    setBearerToken: (token: string, tokenType: string, expires: number) => Promise<void>;
    setRefreshToken: (token: string, tokenType: string, expires: number) => Promise<void>;
    bearerToken: () => string | null;
    accessToken: CookieRef<AccessTokenCookie>;
    refreshToken: CookieRef<RefreshTokenCookie>;
    authConfig: ModuleOptions;
  } = await useAuth();

  const user = computed(() => getOAuthUser());

  const signIn = async () => {
    await navigateTo("/login");
  };

  const wrappedSignOut = async () => {
    if (isSigningOut) return;

    isSigningOut = true;

    removeOAuthUser();

    await setBearerToken("", "", 0);
    await setRefreshToken("", "", 0);

    if (typeof window !== "undefined") {
      localStorage.removeItem("oauth_location_token");
      localStorage.removeItem("oauth_user");
      localStorage.removeItem("api_location_id");
      localStorage.removeItem("location_colors_cache");

      document.cookie = "oauth_access_token=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      document.cookie = "oauth_refresh_token=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      document.cookie = "oauth_user=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    }

    await signOut();

    if (typeof window !== "undefined") {
      await navigateTo("/login");
    }

    isSigningOut = false;
  };

  const refreshAccessToken = async (): Promise<boolean> => {
    if (import.meta.client) {
      if (refreshPromise) {
        return refreshPromise;
      }

      if (refreshMutex) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        return await refreshAccessToken();
      }

      refreshMutex = true;
    }

    const promise = (async () => {
      try {
        if (!refreshToken.value?.token) {
          console.error("❌ [REFRESH] No refresh token available, signing out");
          await wrappedSignOut();
          return false;
        }

        const formData = new FormData();
        formData.append("grant_type", "refresh_token");
        formData.append("refresh_token", refreshToken.value.token);
        formData.append("client_id", config.oauth.clientId);

        const response: Response = await fetch(config.oauth.endpoints.token, {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("❌ [REFRESH] Refresh failed:", {
            status: response.status,
            statusText: response.statusText,
            error: errorText,
          });
          await wrappedSignOut();
          return false;
        }

        const data: AccessTokenResponse = await response.json();

        await setBearerToken(data.access_token, data.token_type, data.expires_in);
        const maxAge = (config.oauth as OAuthConfig).refreshToken?.maxAge || 60 * 60 * 24 * 30;
        await setRefreshToken(data.refresh_token, data.token_type, maxAge);

        return true;
      } catch (error) {
        console.error("💥 [REFRESH] Network error during refresh:", error);
        await wrappedSignOut();
        return false;
      } finally {
        if (import.meta.client) {
          refreshMutex = false;
          refreshPromise = null;
        }
      }
    })();

    if (import.meta.client) {
      refreshPromise = promise;
    }

    return promise;
  };

  const locationToken = useLocalStorage<string | null>("oauth_location_token", null);

  const removeLocationToken = () => {
    locationToken.value = null;
  };

  const retrieveLocationToken = async (currentLocationId: LocationIdentifier) => {
    const fetchLocationToken = async (locationId: LocationIdentifier): Promise<Response> => {
      const formData = new FormData();
      formData.append("location_id", locationId.toString());

      return fetch(`${config.api.oauthUrl}/oauth/token/location`, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          Authorization: bearerToken(),
        } as HeadersInit,
      });
    };

    let response = await fetchLocationToken(currentLocationId);

    if (response.status === 401 && refreshToken.value?.token) {
      await refreshAccessToken();

      response = await fetchLocationToken(currentLocationId);
    }

    if (!response.ok) {
      return;
    }

    const data: LocationTokenResponse = await response.json();

    locationToken.value = data.token;
  };

  const { decodeToken } = useJwt();

  const tokenScopes = async () => {
    if (!accessToken.value?.token) {
      return [];
    }

    try {
      const decodedAccessToken = await decodeToken(accessToken.value.token);

      if (!locationToken.value) {
        return decodedAccessToken.scopes;
      }

      const decodedLocationToken = await decodeToken(locationToken.value);

      const scopes = new Set([...decodedAccessToken.scopes, ...decodedLocationToken.scopes]);

      return Array.from(scopes);
    } catch (error) {
      console.error("[useOAuth] tokenScopes() failed to decode token(s):", error);
      throw error;
    }
  };

  const shouldRefreshToken = (): boolean => {
    if (!accessToken.value?.expiresAt) return false;
    // No refresh token is a valid, intentional state (e.g. impersonation, see
    // checkAndCleanupStaleAuth below) - there is nothing to proactively refresh with,
    // so let such sessions ride until they naturally expire on a real 401 instead of
    // treating a routine "close to expiry" check as a hard failure.
    if (!refreshToken.value?.token) return false;

    const refreshThresholdMs = 5 * 60 * 1000;
    const expiresAt = accessToken.value.expiresAt;

    return expiresAt - Date.now() < refreshThresholdMs;
  };

  const checkAndCleanupStaleAuth = (): boolean => {
    const hasUser = !!getOAuthUser();
    const hasAccessToken = !!accessToken.value?.token;
    const hasRefreshToken = !!refreshToken.value?.token;

    const hasValidAccessToken = hasAccessToken && accessToken.value.token.trim() !== "";
    const hasValidRefreshToken = hasRefreshToken && refreshToken.value.token.trim() !== "";

    if (hasUser && hasValidAccessToken && !hasValidRefreshToken) {
      console.warn(
        "Access token exists but refresh token is missing - this might be intentional (e.g., impersonation)",
      );
      return false;
    }

    if (hasUser && (!hasValidAccessToken || !hasValidRefreshToken)) {
      console.warn("Stale authentication detected: user exists but tokens are missing or invalid, cleaning up", {
        hasUser,
        hasAccessToken,
        hasRefreshToken,
        hasValidAccessToken,
        hasValidRefreshToken,
      });

      removeOAuthUser();

      if (typeof window !== "undefined") {
        localStorage.removeItem("oauth_location_token");
        localStorage.removeItem("oauth_user");
        localStorage.removeItem("api_location_id");

        document.cookie = "oauth_user=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      }

      return true;
    }

    return false;
  };

  let signOutTimeout: NodeJS.Timeout | null = null;

  watch(user, async (newValue, oldValue) => {
    if (signOutTimeout) {
      clearTimeout(signOutTimeout);
      signOutTimeout = null;
    }

    if (!newValue && oldValue && !isSigningOut) {
      signOutTimeout = setTimeout(async () => {
        if (!user.value && !isSigningOut) {
          console.error("User state became undefined after delay, signing out", {
            timestamp: new Date().toISOString(),
            oldValue: oldValue ? { id: oldValue.id, email: oldValue.email } : null,
          });
          await wrappedSignOut();
        }
      }, 1000);
    }
  });

  return {
    user,
    signIn,
    signOut: wrappedSignOut,
    setBearerToken,
    setRefreshToken,
    bearerToken,
    accessToken,
    refreshToken,
    authConfig,
    refreshAccessToken,
    retrieveLocationToken,
    removeLocationToken,
    tokenScopes,
    testRefreshToken: () => {
      return refreshAccessToken();
    },
    corruptAccessToken: () => {
      if (accessToken.value?.token) {
        const corruptedToken = accessToken.value.token.slice(0, -10) + "CORRUPTED";
        accessToken.value.token = corruptedToken;
      }
    },
    shouldRefreshToken,
    checkAndCleanupStaleAuth,
  };
}
